import { useInView } from "@/hooks/useAnimations";
import { UserRound, Heart, Briefcase, Home } from "lucide-react";

const audiences = [
  {
    icon: UserRound,
    title: "Pflegebedürftige",
    desc: "Sie haben einen Pflegegrad und benötigen Ihre regelmäßige Pflichtberatung nach §37.3 SGB XI — einfach per Video von zuhause.",
    color: "text-cyan-700",
    bg: "bg-cyan-50",
    border: "border-cyan-100",
  },
  {
    icon: Heart,
    title: "Pflegende Angehörige",
    desc: "Sie pflegen einen Angehörigen und haben Fragen zu Leistungen, Entlastungsmöglichkeiten oder dem Umgang mit der Pflegekasse.",
    color: "text-green-600",
    bg: "bg-green-50",
    border: "border-green-100",
  },
  {
    icon: Briefcase,
    title: "Berufstätige Familien",
    desc: "Zwischen Job und Familie bleibt wenig Zeit. Unsere flexible Online-Beratung passt in jeden Terminkalender.",
    color: "text-cyan-700",
    bg: "bg-cyan-50",
    border: "border-cyan-100",
  },
  {
    icon: Home,
    title: "Menschen in NRW",
    desc: "Wir kennen die regionalen Angebote und Ansprechpartner in Nordrhein-Westfalen und beraten Sie passgenau.",
    color: "text-green-600",
    bg: "bg-green-50",
    border: "border-green-100",
  },
];

export default function TargetAudience() {
  const [ref, isInView] = useInView();

  return (
    <section
      ref={ref}
      id="fuer-wen"
      data-testid="target-audience-section"
      className="py-20 lg:py-28 bg-white scroll-mt-[120px] xl:scroll-mt-[96px]"
      aria-label="Für wen ist die Beratung"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center max-w-2xl mx-auto mb-14 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-sm font-semibold text-green-600 bg-green-50 px-4 py-1.5 rounded-full mb-4">
            Für wen?
          </span>
          <h2 className="font-['Manrope'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4">
            Ist die Beratung für mich?
          </h2>
          <p className="text-base sm:text-lg text-slate-500 leading-relaxed">
            Unsere Online-Pflegeberatung richtet sich an alle, die Unterstützung rund um das Thema Pflege benötigen.
          </p>
        </div>

        {/* Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {audiences.map((a, i) => (
            <div
              key={i}
              data-testid={`audience-card-${i}`}
              className={`bg-white border ${a.border} rounded-2xl p-6 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-500 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: isInView ? `${i * 120}ms` : '0ms' }}
            >
              <div className={`h-14 w-14 ${a.bg} rounded-2xl flex items-center justify-center mx-auto mb-5`}>
                <a.icon className={`h-7 w-7 ${a.color}`} />
              </div>
              <h3 className="font-['Manrope'] text-lg font-semibold text-slate-800 mb-3">
                {a.title}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                {a.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
